/**
 * 连接质量监控
 * 延迟/丢包率/带宽估算
 */
export interface ConnectionQuality {
  latency: number
  jitter: number
  packetLoss: number
  bandwidth: number
  score: number
}

export class ConnectionQualityMonitor {
  private latencies: number[] = []
  private sentPings = 0
  private receivedPongs = 0
  private bytesTransferred = 0
  private transferStartTime = 0
  private maxSamples: number

  constructor(maxSamples: number = 50) {
    this.maxSamples = maxSamples
  }

  /** 记录往返延迟（RTT） */
  recordLatency(ms: number): void {
    this.latencies.push(ms)
    if (this.latencies.length > this.maxSamples) this.latencies.shift()
  }

  /** 记录 ping 发送 */
  recordPingSent(): void {
    this.sentPings++
  }

  /** 记录 pong 接收 */
  recordPongReceived(): void {
    this.receivedPongs++
  }

  /** 记录数据传输（用于带宽估算） */
  recordTransfer(bytes: number): void {
    if (this.transferStartTime === 0) this.transferStartTime = Date.now()
    this.bytesTransferred += bytes
  }

  /** 获取连接质量报告 */
  getQuality(): ConnectionQuality {
    const latency = this.getAvgLatency()
    const jitter = this.getJitter()
    const packetLoss = this.getPacketLoss()
    const bandwidth = this.getBandwidth()
    const score = this.calculateScore(latency, jitter, packetLoss)

    return { latency, jitter, packetLoss, bandwidth, score }
  }

  /** 平均延迟 */
  getAvgLatency(): number {
    if (this.latencies.length === 0) return 0
    return Math.round(this.latencies.reduce((a, b) => a + b, 0) / this.latencies.length)
  }

  /** 延迟抖动（标准差） */
  getJitter(): number {
    if (this.latencies.length < 2) return 0
    const avg = this.getAvgLatency()
    const variance = this.latencies.reduce((sum, l) => sum + Math.pow(l - avg, 2), 0) / this.latencies.length
    return Math.round(Math.sqrt(variance))
  }

  /** 丢包率（百分比） */
  getPacketLoss(): number {
    if (this.sentPings === 0) return 0
    return Math.round((1 - this.receivedPongs / this.sentPings) * 100)
  }

  /** 带宽估算（KB/s） */
  getBandwidth(): number {
    if (this.bytesTransferred === 0 || this.transferStartTime === 0) return 0
    const elapsed = (Date.now() - this.transferStartTime) / 1000
    if (elapsed === 0) return 0
    return Math.round(this.bytesTransferred / 1024 / elapsed)
  }

  /** 重置统计 */
  reset(): void {
    this.latencies = []
    this.sentPings = 0
    this.receivedPongs = 0
    this.bytesTransferred = 0
    this.transferStartTime = 0
  }

  private calculateScore(latency: number, jitter: number, packetLoss: number): number {
    let score = 100
    if (latency > 50) score -= Math.min(30, (latency - 50) / 10)
    if (jitter > 20) score -= Math.min(20, (jitter - 20) / 5)
    score -= packetLoss * 2
    return Math.max(0, Math.round(score))
  }
}
